import { brightspotConfig } from '../../../config'

type QueryFunction = (variables: Record<string, unknown>) => Promise<{ results: unknown[] }>
type VariableFunction = (chunk: string[]) => Record<string, unknown>

// persisted queries don't handle larger pages
const BRIGHTSPOT_PAGE_LIMIT = brightspotConfig.query.manualChunkSize

/**
 * Execute a brightspot query in chunks, with data provided by the caller. Will re-throw any exceptions caused by the
 * query function provided.
 *
 * @param ids ids to be chunked
 * @param queryFunction function to be called with chunked variables, should return a list of results
 * @param variableFunction function to create the variables necessary
 * @returns the results of each chunk combined into a single array
 */
export async function loadChunks(
  ids: string[],
  queryFunction: QueryFunction,
  variableFunction: VariableFunction
): Promise<unknown[]> {
  if (!ids || ids.length === 0) {
    return []
  }

  let start = 0
  let end = Math.min(ids.length, BRIGHTSPOT_PAGE_LIMIT)
  let keepGoing = true

  // Execute brightspot query, 100 at a time
  const promises: Promise<{ results: unknown[] }>[] = []
  while (keepGoing) {
    const chunk = ids.slice(start, end)
    const variables = variableFunction(chunk)
    promises.push(queryFunction(variables))

    start = end
    end = Math.min(start + BRIGHTSPOT_PAGE_LIMIT, ids.length)
    keepGoing = start < ids.length
  }

  const chunks = await Promise.all(promises)

  return chunks.flatMap((chunkResult) => chunkResult.results)
}
